import {
  Building2,
  Check,
  Copy,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  adminClient,
  type IntegrationAuthMode,
  type IntegrationConnectionScope,
  type IntegrationDraftAuth,
  type IntegrationDraftCredentialField,
} from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SecretInput } from '@/components/ui/secret-input';
import { cn } from '@/lib/utils';

import {
  credentialFieldsForAuth,
  errorMessage,
  labelFromCredentialKey,
  modesForAuth,
  orgHoldsCredentials,
  pasteSecretPlaceholder,
  relativeTime,
  scopeMode,
  type StepProps,
} from '../builderModel';
import { Field, StepFrame } from '../builderUi';

const SCOPES: Array<{ id: IntegrationConnectionScope; title: string; body: string; icon: typeof Users }> = [
  {
    id: 'platform',
    title: 'One RapidSOS account',
    body: 'RapidSOS stores one credential here and every organization that turns it on shares it. Good for data services billed through Studio.',
    icon: Users,
  },
  {
    id: 'org',
    title: 'Each organization',
    body: 'An org admin signs in to the organization’s own account, or enters its API key, login or OAuth client, once, and every app in the org uses it.',
    icon: Building2,
  },
  {
    id: 'user',
    title: 'Each user',
    body: 'Every person signs in to their own account with OAuth or enters their own API key, in Studio or from inside an app, and calls act as them: their mailbox, their calendar, their records.',
    icon: User,
  },
];

const MODE_CHOICES: Array<{ id: IntegrationAuthMode; title: string; body: string }> = [
  { id: 'platform', title: 'One RapidSOS account', body: 'Only once RapidSOS has stored its credential here.' },
  { id: 'organization', title: 'Each organization', body: 'An org admin connects the organization’s own account.' },
  { id: 'user', title: 'Each user', body: 'Every person signs in, or enters their own key.' },
];

/** An authentication kind, with OAuth client credentials as its own choice. */
type AuthChoice = IntegrationDraftAuth['kind'] | 'client_credentials';

const AUTH_KINDS: Array<{ id: AuthChoice; title: string; body: string }> = [
  { id: 'none', title: 'No authentication', body: 'A public API, or one reached over a private network.' },
  { id: 'api_key', title: 'API key', body: 'A static key sent in a header or query parameter.' },
  { id: 'bearer', title: 'Bearer token', body: 'Sent as Authorization: Bearer <token>.' },
  { id: 'basic', title: 'Basic auth', body: 'A username and password pair.' },
  {
    id: 'oauth2',
    title: 'OAuth 2.0 sign-in',
    body: 'A person signs in with the service (authorization code with PKCE). Tokens are kept per organization or per user and refreshed for you.',
  },
  {
    id: 'client_credentials',
    title: 'OAuth client credentials',
    body: 'Studio trades a client ID and secret for a short-lived access token. Nobody has to sign in.',
  },
];

/** Which authentication kinds make sense for each scope. */
const AUTH_FOR_SCOPE: Record<IntegrationConnectionScope, AuthChoice[]> = {
  platform: ['none', 'api_key', 'bearer', 'basic', 'client_credentials'],
  org: ['oauth2', 'api_key', 'bearer', 'basic', 'client_credentials'],
  user: ['oauth2', 'api_key', 'bearer', 'basic'],
};

function authChoice(auth: IntegrationDraftAuth): AuthChoice {
  return auth.kind === 'oauth2' && auth.grant === 'client_credentials' ? 'client_credentials' : auth.kind;
}

function defaultAuth(choice: AuthChoice, current: IntegrationDraftAuth): IntegrationDraftAuth {
  if (choice === 'api_key') return { kind: choice, in: current.in ?? 'header', paramName: current.paramName || 'X-API-Key' };
  if (choice === 'client_credentials') {
    return {
      kind: 'oauth2',
      grant: 'client_credentials',
      tokenUrl: current.tokenUrl ?? '',
      scopes: current.scopes ?? [],
      scopeSeparator: ' ',
      clientAuth: 'basic',
    };
  }
  if (choice === 'oauth2') {
    return {
      kind: 'oauth2',
      grant: 'authorization_code',
      authorizationUrl: current.authorizationUrl ?? '',
      tokenUrl: current.tokenUrl ?? '',
      scopes: current.scopes ?? [],
      scopeSeparator: current.scopeSeparator ?? ' ',
      pkce: current.pkce ?? true,
      clientAuth: current.clientAuth ?? 'basic',
    };
  }
  return { kind: choice };
}

function CopyField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <Input readOnly value={value} aria-label={label} className="font-mono text-xs" onFocus={(event) => event.target.select()} />
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? <Check className="mr-2 h-3.5 w-3.5" /> : <Copy className="mr-2 h-3.5 w-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

function ClientAuthField({ auth, setAuth, readOnly }: { auth: IntegrationDraftAuth; setAuth: (auth: IntegrationDraftAuth) => void; readOnly?: boolean }) {
  return (
    <Field
      label="Send the client ID and secret"
      hint="Most services take them in the Authorization header, which the OAuth standard recommends. Choose the request body only if the service’s docs say so."
    >
      <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
        {(
          [
            { id: 'basic', label: 'Authorization header' },
            { id: 'body', label: 'Request body' },
          ] as const
        ).map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={readOnly}
            onClick={() => setAuth({ ...auth, clientAuth: option.id })}
            aria-pressed={(auth.clientAuth ?? 'basic') === option.id}
            className={cn(
              'rounded px-3 py-1.5 transition-colors',
              (auth.clientAuth ?? 'basic') === option.id ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </Field>
  );
}

function SecretRow({
  draftId,
  field,
  status,
  onStored,
  onRemoved,
}: {
  draftId: string;
  field: IntegrationDraftCredentialField;
  status?: { updatedAt: string; updatedBy: string };
  onStored: (status: { key: string; updatedAt: string; updatedBy: string }) => void;
  onRemoved: (key: string) => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!status);

  const store = async () => {
    setBusy(true);
    try {
      const stored = await adminClient.setIntegrationDraftSecret(draftId, field.key, value);
      onStored(stored);
      setValue('');
      setEditing(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await adminClient.deleteIntegrationDraftSecret(draftId, field.key);
      onRemoved(field.key);
      setEditing(true);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 border-t border-border px-5 py-4 first:border-t-0 sm:flex-row sm:items-center">
      <div className="min-w-0 sm:w-56">
        <div className="flex items-center gap-2">
          <code className="truncate font-mono text-xs font-semibold">{field.key}</code>
          {field.required ? null : <span className="text-[10px] text-muted-foreground">optional</span>}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {status ? (
            <span className="inline-flex items-center gap-1 text-success">
              <Check className="h-3 w-3" />
              Stored {relativeTime(status.updatedAt)} · {status.updatedBy}
            </span>
          ) : (
            'Not set'
          )}
        </div>
      </div>
      {editing ? (
        <div className="flex flex-1 items-center gap-2">
          <div className="flex-1">
            <SecretInput
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={pasteSecretPlaceholder(field, Boolean(status))}
              aria-label={`${field.key} value`}
              className="font-mono text-xs"
            />
          </div>
          <Button size="sm" onClick={() => void store()} disabled={!value.trim() || busy}>
            {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Lock className="mr-2 h-3.5 w-3.5" />}
            Store
          </Button>
          {status ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-between gap-2">
          <span className="font-mono text-xs tracking-widest text-muted-foreground" aria-hidden="true">
            ••••••••••••••••
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Replace
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void remove()} disabled={busy} aria-label={`Remove ${field.key}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function CredentialsStep({ draft, readOnly, saving, save, replace, next }: StepProps) {
  const [scope, setScope] = useState<IntegrationConnectionScope>(draft.connectionScope ?? 'platform');
  const [modes, setModes] = useState<IntegrationAuthMode[]>(draft.authModes ?? [scopeMode(draft.connectionScope ?? 'platform')]);
  const [auth, setAuth] = useState<IntegrationDraftAuth>(draft.auth);
  const [scopesText, setScopesText] = useState((draft.auth.scopes ?? []).join(' '));
  const choice = authChoice(auth);
  const [fields, setFields] = useState<IntegrationDraftCredentialField[]>(draft.credentials);
  const offered = modesForAuth(auth).filter((mode) => mode === scopeMode(scope) || modes.includes(mode));
  const dirty =
    scope !== (draft.connectionScope ?? 'platform') ||
    JSON.stringify([...offered].sort()) !== JSON.stringify([...(draft.authModes ?? [scopeMode(draft.connectionScope ?? 'platform')])].sort()) ||
    JSON.stringify(auth) !== JSON.stringify(draft.auth) ||
    JSON.stringify(fields) !== JSON.stringify(draft.credentials);

  const setField = (index: number, patch: Partial<IntegrationDraftCredentialField>) =>
    setFields((current) => current.map((field, i) => (i === index ? { ...field, ...patch } : field)));

  const chooseAuth = (next: AuthChoice) => {
    const nextAuth = defaultAuth(next, auth);
    setAuth(nextAuth);
    setScopesText((nextAuth.scopes ?? []).join(' '));
    setFields((current) => credentialFieldsForAuth(nextAuth.kind, current));
  };

  const chooseScope = (nextScope: IntegrationConnectionScope) => {
    setScope(nextScope);
    // Keep the authentication when it still fits; otherwise move to the first one that does.
    if (!AUTH_FOR_SCOPE[nextScope].includes(choice)) chooseAuth(AUTH_FOR_SCOPE[nextScope][0]);
  };

  const persist = () => {
    const removed = draft.secrets.filter((secret) => !fields.some((field) => field.key === secret.key));
    if (removed.length && !window.confirm(`Removing ${removed.map((secret) => secret.key).join(', ')} also deletes the stored value. Continue?`)) {
      return Promise.resolve(undefined);
    }
    return save({ connectionScope: scope, authModes: offered, auth, credentials: fields });
  };

  const toggleMode = (mode: IntegrationAuthMode, checked: boolean) =>
    setModes((current) => (checked ? [...new Set([...current, mode])] : current.filter((entry) => entry !== mode)));

  const orgHeld = orgHoldsCredentials({ connectionScope: draft.connectionScope ?? 'platform', auth: draft.auth, authModes: draft.authModes });
  const statusFor = (key: string) => draft.secrets.find((secret) => secret.key === key);
  // Show the new status at once, then refetch so the checklist reflects it.
  const updateSecrets = (secrets: typeof draft.secrets) => {
    replace({ ...draft, secrets });
    void adminClient.getIntegrationDraft(draft.draftId).then(replace).catch(() => undefined);
  };

  return (
    <StepFrame
      title="Connection"
      description="Choose whose account the integration uses, how the service authenticates, and which values Studio needs. Stored values go straight into the RapidSOS vault and can never be read back."
      dirty={dirty}
      saving={saving}
      readOnly={readOnly}
      onSave={() => void persist()}
      onContinue={async () => {
        if (!dirty) {
          next();
          return;
        }
        if (await persist()) next();
      }}
    >
      <Field
        label="Whose account"
        hint="This is what customers see on the integration page: nothing to set up, a Connect button for an org admin, or a Connect button for each person."
      >
        <div className="grid gap-3 lg:grid-cols-3">
          {SCOPES.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                type="button"
                disabled={readOnly}
                onClick={() => chooseScope(option.id)}
                aria-pressed={scope === option.id}
                className={cn(
                  'rounded-xl border p-4 text-left transition-colors',
                  scope === option.id ? 'border-brand bg-brand-muted/40' : 'border-border hover:bg-muted/30',
                )}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Icon className="h-4 w-4 text-brand" />
                  {option.title}
                </span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">{option.body}</span>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Authentication">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {AUTH_KINDS.filter((kind) => AUTH_FOR_SCOPE[scope].includes(kind.id)).map((kind) => (
            <button
              key={kind.id}
              type="button"
              disabled={readOnly}
              onClick={() => chooseAuth(kind.id)}
              className={cn(
                'rounded-xl border p-4 text-left transition-colors',
                choice === kind.id ? 'border-brand bg-brand-muted/40' : 'border-border hover:bg-muted/30',
              )}
            >
              <span className="block text-sm font-medium">{kind.title}</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">{kind.body}</span>
            </button>
          ))}
        </div>
      </Field>

      <Field
        label="Admins may also choose"
        hint="Org admins pick one of these on the integration's page under Studio → Integrations, and each app's managers may pick another for their app. The account above is the default, and connectors published before this keep it alone."
      >
        <div className="flex flex-wrap gap-6 text-xs">
          {MODE_CHOICES.filter((option) => option.id !== scopeMode(scope)).map((option) => {
            const possible = modesForAuth(auth).includes(option.id);
            return (
              <label key={option.id} className={cn('flex items-start gap-2', possible && !readOnly ? 'cursor-pointer' : 'opacity-60')}>
                <input
                  type="checkbox"
                  checked={possible && modes.includes(option.id)}
                  disabled={readOnly || !possible}
                  onChange={(event) => toggleMode(option.id, event.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-[var(--brand)]"
                />
                <span>
                  <span className="block font-medium text-foreground">{option.title}</span>
                  <span className="block text-muted-foreground">{possible ? option.body : 'Not with this authentication.'}</span>
                </span>
              </label>
            );
          })}
        </div>
      </Field>

      {auth.kind === 'api_key' ? (
        <div className="grid gap-4 sm:grid-cols-[200px_minmax(0,1fr)]">
          <Field label="Send the key in">
            <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
              {(['header', 'query'] as const).map((location) => (
                <button
                  key={location}
                  type="button"
                  disabled={readOnly}
                  onClick={() => setAuth({ ...auth, in: location })}
                  className={cn(
                    'rounded px-3 py-1.5 capitalize transition-colors',
                    auth.in === location ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {location}
                </button>
              ))}
            </div>
          </Field>
          <Field id="auth-param" label={auth.in === 'query' ? 'Query parameter' : 'Header name'}>
            <Input
              id="auth-param"
              value={auth.paramName ?? ''}
              disabled={readOnly}
              onChange={(event) => setAuth({ ...auth, paramName: event.target.value })}
              className="font-mono text-sm"
            />
          </Field>
        </div>
      ) : null}

      {choice === 'client_credentials' ? (
        <div className="space-y-4">
          <Field
            id="auth-token-url"
            label="Token URL"
            hint="Where Studio asks the service for an access token before each call. It must start with https://. Tokens are reused until they are about to expire."
          >
            <Input
              id="auth-token-url"
              value={auth.tokenUrl ?? ''}
              disabled={readOnly}
              onChange={(event) => setAuth({ ...auth, tokenUrl: event.target.value.trim() })}
              placeholder="https://login.example.com/oauth/token"
              className="font-mono text-sm"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="auth-scopes" label="Scopes" optional hint="What the token may do, separated by spaces. Leave empty if the service does not use scopes.">
              <Input
                id="auth-scopes"
                value={scopesText}
                disabled={readOnly}
                onChange={(event) => {
                  setScopesText(event.target.value);
                  setAuth({ ...auth, scopes: event.target.value.split(/\s+/).filter(Boolean) });
                }}
                placeholder="records.read records.write"
                className="font-mono text-sm"
              />
            </Field>
            <Field id="auth-audience" label="Audience" optional hint="Only if the service’s docs ask for one; it names the API the token is for.">
              <Input
                id="auth-audience"
                value={auth.audience ?? ''}
                disabled={readOnly}
                onChange={(event) => setAuth({ ...auth, audience: event.target.value || undefined })}
                placeholder="https://api.example.com"
                className="font-mono text-sm"
              />
            </Field>
          </div>
          <ClientAuthField auth={auth} setAuth={setAuth} readOnly={readOnly} />
        </div>
      ) : null}

      {choice === 'oauth2' ? (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Field id="auth-authorize-url" label="Authorization URL" hint="The service’s sign-in page. It must start with https://.">
              <Input
                id="auth-authorize-url"
                value={auth.authorizationUrl ?? ''}
                disabled={readOnly}
                onChange={(event) => setAuth({ ...auth, authorizationUrl: event.target.value.trim() })}
                placeholder="https://login.example.com/oauth2/authorize"
                className="font-mono text-sm"
              />
            </Field>
            <Field id="auth-signin-token-url" label="Token URL" hint="Where Studio trades the sign-in for tokens and refreshes them. It must start with https://.">
              <Input
                id="auth-signin-token-url"
                value={auth.tokenUrl ?? ''}
                disabled={readOnly}
                onChange={(event) => setAuth({ ...auth, tokenUrl: event.target.value.trim() })}
                placeholder="https://login.example.com/oauth2/token"
                className="font-mono text-sm"
              />
            </Field>
          </div>
          <Field
            id="auth-signin-scopes"
            label="Scopes"
            optional
            hint="Separated by spaces. Ask for the least the enabled operations need, plus offline access if the service needs it to issue refresh tokens."
          >
            <Input
              id="auth-signin-scopes"
              value={scopesText}
              disabled={readOnly}
              onChange={(event) => {
                setScopesText(event.target.value);
                setAuth({ ...auth, scopes: event.target.value.split(/\s+/).filter(Boolean) });
              }}
              placeholder="contacts.read offline_access"
              className="font-mono text-sm"
            />
          </Field>
          <div className="flex flex-wrap gap-6 text-xs">
            {[
              { label: 'Send a PKCE challenge', checked: auth.pkce !== false, onChange: (checked: boolean) => setAuth({ ...auth, pkce: checked }) },
              { label: 'Separate scopes with commas', checked: auth.scopeSeparator === ',', onChange: (checked: boolean) => setAuth({ ...auth, scopeSeparator: checked ? ',' : ' ' }) },
            ].map((toggle) => (
              <label key={toggle.label} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={toggle.checked}
                  disabled={readOnly}
                  onChange={(event) => toggle.onChange(event.target.checked)}
                  className="h-3.5 w-3.5 accent-[var(--brand)]"
                />
                {toggle.label}
              </label>
            ))}
          </div>
          <ClientAuthField auth={auth} setAuth={setAuth} readOnly={readOnly} />
          <Field
            label="Redirect URI"
            hint="Register this exact URI in the OAuth app you create with the service, then store that app’s client ID and secret in the vault below. Every organization and person signs in through that one app."
          >
            <CopyField value={draft.oauthRedirectUri} label="OAuth redirect URI" />
          </Field>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-border">
        <div className="flex items-center justify-between gap-3 bg-card/40 px-5 py-3">
          <div>
            <div className="text-sm font-semibold">Credential fields</div>
            <div className="text-xs text-muted-foreground">
              The values the service needs. Keys are UPPER_SNAKE_CASE. They stay in the Studio broker — no app ever receives them.
            </div>
          </div>
          {readOnly ? null : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFields((current) => [...current, { key: '', label: '', description: '', exposure: 'server', required: true }])}
            >
              <Plus className="mr-2 h-3.5 w-3.5" />
              Add field
            </Button>
          )}
        </div>
        {fields.length === 0 ? (
          <div className="border-t border-border px-5 py-6 text-sm text-muted-foreground">This integration needs no credentials.</div>
        ) : (
          <div
            aria-hidden="true"
            className="hidden gap-3 border-t border-border px-5 pt-3 text-[11px] font-medium text-muted-foreground lg:grid lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1.4fr)_140px]"
          >
            <span>Key</span>
            <span>Name customers see</span>
            <span>Help text</span>
            <span />
          </div>
        )}
        {fields.map((field, index) => (
          <div key={index} className="grid gap-3 border-t border-border px-5 py-4 lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1.4fr)_140px]">
            <Input
              aria-label="Key"
              value={field.key}
              disabled={readOnly}
              onChange={(event) => setField(index, { key: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
              placeholder="API_KEY"
              className="font-mono text-xs"
            />
            <Input
              aria-label="Name customers see"
              value={field.label}
              disabled={readOnly}
              onChange={(event) => setField(index, { label: event.target.value })}
              placeholder={labelFromCredentialKey(field.key) || 'Name customers see'}
            />
            <Input
              aria-label="Help text"
              value={field.description}
              disabled={readOnly}
              onChange={(event) => setField(index, { description: event.target.value })}
              placeholder="Where to find it"
            />
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={field.required}
                  disabled={readOnly}
                  onChange={(event) => setField(index, { required: event.target.checked })}
                  className="h-3.5 w-3.5 accent-[var(--brand)]"
                />
                Required
              </label>
              {readOnly ? null : (
                <Button variant="ghost" size="icon" aria-label="Remove field" onClick={() => setFields((current) => current.filter((_, i) => i !== index))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <div className="flex items-start gap-3 bg-card/40 px-5 py-4">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <div>
            <div className="text-sm font-semibold">Vault</div>
            <div className="text-xs leading-5 text-muted-foreground">
              Values are write-only. Each one is encrypted into the RapidSOS vault and can never be shown
              again — not here, not by the API. To change one, store a new value.
            </div>
          </div>
        </div>
        {orgHeld ? (
          <div className="border-t border-border px-5 py-6 text-sm text-muted-foreground">
            Each organization enters these values when an org admin connects it in Studio’s Integrations page. RapidSOS stores
            none of them. To test here, paste a value in the Test operation dialog; it is used for that call only.
          </div>
        ) : draft.credentials.length === 0 ? (
          <div className="border-t border-border px-5 py-6 text-sm text-muted-foreground">
            {fields.length ? 'Save the credential fields first, then store their values here.' : 'Nothing to store.'}
          </div>
        ) : (
          <div className="border-t border-border">
            {draft.credentials.map((field) => (
              <SecretRow
                key={field.key}
                draftId={draft.draftId}
                field={field}
                status={statusFor(field.key)}
                onStored={(stored) => updateSecrets([...draft.secrets.filter((secret) => secret.key !== stored.key), stored])}
                onRemoved={(key) => updateSecrets(draft.secrets.filter((secret) => secret.key !== key))}
              />
            ))}
          </div>
        )}
      </div>
    </StepFrame>
  );
}
